import styled, { css, keyframes } from "styled-components";

// Animations
const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

// Layout Components
export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  flex-wrap: wrap;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--accent-light);
`;

export const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0;
`;

export const SectionActions = styled.div`
  display: flex;
  gap: 15px;
`;

export const Button = styled.button`
  padding: 12px 25px;
  border: none;
  border-radius: 20px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;

  ${(props) =>
    props.$small &&
    `
    padding: 8px 16px;
    font-size: 12px;
  `}

  ${(props) =>
    props.$primary &&
    `
    background: var(--gradient-primary);
    color: var(--surface);
    
    &:hover:not(:disabled) {
      background: var(--accent-dark);
      color: var(--surface);
      transform: translateY(-2px);
    }
  `}

  ${(props) =>
    props.$secondary &&
    `
    background: transparent;
    color: var(--text-secondary);
    border: 2px solid var(--accent-light);
    
    &:hover:not(:disabled) {
      background: var(--accent-light);
      border-color: var(--accent);
      color: var(--accent);
      transform: translateY(-2px);
    }
  `}

     svg {
    animation: ${(props) =>
      props.$loading
        ? css`
            ${spin} 1s ease-in-out infinite
          `
        : "none"};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;
export const PaginationButton = styled(Button)`
  padding: 8px 16px;
  font-size: 12px;
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  @media (max-width: 825px) {
    border-radius: 12px;
    border-width: 1px;
  }
`;

// Filter Components
export const Filters = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 25px;
  flex-wrap: wrap;
`;

export const FilterInput = styled.input`
  padding: 12px 20px;
  border: 2px solid var(--accent-light);
  border-radius: 25px;
  font-family: inherit;
  max-height: 38px;
  font-size: 14px;
  background: var(--surface);
  color: var(--text-primary);
  transition: all 0.3s ease;
  outline: none;
  width: 100%;
  max-width: 300px;

  &:focus {
    border-color: var(--accent);
    background: var(--surface);
    box-shadow: 0 0 0 3px rgba(124, 14, 51, 0.1);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

export const OrderStatusBadge = styled.span`
  display: inline-block;
  margin-top: 4px;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${(props) =>
    props.$status === "completed"
      ? "rgba(16, 185, 129, 0.12)"
      : props.$status === "pending"
        ? "rgba(251, 191, 36, 0.15)"
        : props.$status === "processing"
          ? "rgba(59, 130, 246, 0.12)"
          : "rgba(239, 68, 68, 0.12)"};
  color: ${(props) =>
    props.$status === "completed"
      ? "#059669"
      : props.$status === "pending"
        ? "#b45309"
        : props.$status === "processing"
          ? "#2563eb"
          : "#dc2626"};
`;

// Table Components

export const DataTableScroll = styled.div`
  width: 100% !important;
  overflow-x: auto !important;
  overflow-y: visible !important;
  border-radius: 15px;
  box-shadow: var(--shadow-soft);
  max-width: none !important;
  min-width: 0 !important;
  flex-shrink: 0 !important;
`;

export const DataTable = styled.div`
  display: grid;
  grid-template-columns: 200px 256px 1fr 150px 120px;

  min-width: 986px;

  background: var(--surface);
  border-radius: 15px;
  border: 2px solid var(--accent-light);
  overflow: hidden;
`;

export const TableHeader = styled.div`
  padding: 15px 12px;
  border-bottom: 2px solid var(--accent-light);
  background-color: var(--accent);
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  white-space: nowrap;
  letter-spacing: 1px;
  color: white;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: ${(props) => props.$align || "left"};
`;

export const TableRow = styled.div`
  display: contents;
  cursor: pointer;
  &:hover > div {
    background: rgba(124, 14, 51, 0.05);
  }
`;

export const TableCell = styled.div`
  white-space: nowrap;
  padding: 15px 12px;
  border-bottom: 1px solid var(--accent-light);
  display: flex;
  align-items: center;
  text-align: ${(props) => props.$align || "left"};
`;

export const LoadingCell = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 20px;
`;

// Order Components
export const OrderNumber = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
`;

export const OrderId = styled.div`
  font-size: 11px;
  color: var(--text-muted);
  font-family: monospace;
`;

// User Components
export const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const UserAvatar = styled.div`
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: var(--gradient-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 12px;
  color: white;
  text-transform: uppercase;
`;

export const UserName = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
`;

export const UserEmail = styled.div`
  font-size: 12px;
  color: var(--text-muted);
`;

// Course Components
export const CourseItem = styled.div`
  font-size: 13px;
  color: ${(props) =>
    props.$empty ? "var(--text-muted)" : "var(--text-secondary)"};
  margin-bottom: ${(props) => (props.$hasMargin ? "5px" : "0")};
  /* white-space: nowrap; */
`;

// Price Components
export const PriceCell = styled(TableCell)`
  text-align: right;
  font-weight: 600;
  font-size: 14px;
  color: var(--success);
  /* white-space: nowrap; */
`;

// Date Components
export const DateCell = styled.div`
  color: var(--text-secondary);
  font-size: 13px;
`;

export const TimeCell = styled.span`
  font-size: 11px;
  color: var(--text-muted);
`;

// Action Components
export const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
`;

export const ActionBtn = styled.button`
  max-height: 31.33px;
  padding: 8px;
  border: 1px solid;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  ${(props) => {
    switch (props.$type) {
      case "view":
        return `
          background: var(--info);
          border-color: var(--info);
          color: white;
          
          &:hover {
            background: var(--accent);
            border-color: var(--accent-light);
          }
        `;
      case "edit":
        return `
          background: var(--warning);
          border-color: var(--warning);
          color: white;
          
          &:hover {
            background: var(--accent-dark);
            border-color: var(--warning-dark);
          }
        `;
      case "delete":
        return `
          background: var(--error);
          border-color: var(--error);
          color: white;
          
          &:hover {
            background: var(--error-dark);
            border-color: var(--error-dark);
          }
        `;
      default:
        return `
          border-color: var(--accent-light);
          color: var(--text-secondary);
          
          &:hover {
            background: var(--accent-light);
            border-color: var(--accent);
            color: var(--accent);
          }
        `;
    }
  }}
`;

// Pagination Components
export const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 25px;
  padding: 20px;
  background-color: var(--surface);
  border-radius: 12px;
  box-shadow: var(--shadow-soft);
  border: 1px solid var(--accent-light);
  gap: 12px;
  @media (max-width: 490px) {
    flex-direction: column;
    justify-content: center;
  }
`;

export const PaginationInfo = styled.div`
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;

  strong {
    color: var(--accent);
  }
`;

export const PaginationControls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

export const PageNumber = styled.button`
  padding: 6px 10px;
  border: ${(props) =>
    props.$active
      ? "2px solid var(--accent)"
      : "1px solid var(--accent-light)"};
  background-color: ${(props) =>
    props.$active ? "var(--accent)" : "var(--surface)"};
  color: ${(props) => (props.$active ? "white" : "var(--text-secondary)")};
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: ${(props) => (props.$active ? "600" : "400")};
  transition: all 0.2s ease;
  min-width: 32px;

  &:hover {
    background-color: ${(props) =>
      props.$active ? "var(--accent)" : "var(--accent-light)"};
    border-color: var(--accent);
  }
`;

// Modal Components
export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const ModalContent = styled.div`
  background-color: var(--surface);
  border-radius: 12px;
  padding: 30px;
  max-width: 800px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  border-bottom: 1px solid var(--accent-light);
  padding-bottom: 15px;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  color: var(--text-primary);
  font-size: 24px;
  font-weight: 600;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--text-muted);
  padding: 5px;
`;

export const ModalGrid = styled.div`
  display: grid;
  gap: 25px;
`;

// Order Details Components
export const OrderHeaderGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  padding: 20px;
  background-color: var(--background);
  border-radius: 8px;
`;

export const InfoLabel = styled.label`
  display: block;
  font-size: 12px;
  color: var(--text-muted);
  margin-bottom: 5px;
  text-transform: uppercase;
  font-weight: 600;
`;

export const InfoValue = styled.div`
  font-size: ${(props) => (props.$accent ? "18px" : "14px")};
  font-weight: ${(props) => (props.$accent ? "600" : "normal")};
  color: ${(props) =>
    props.$accent ? "var(--accent)" : "var(--text-primary)"};
`;

export const SectionHeading = styled.h3`
  margin-bottom: 15px;
  color: var(--text-primary);
  font-size: 18px;
`;

export const CustomerGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  padding: 20px;
  background-color: var(--background);
  border-radius: 8px;
`;

export const AddressSection = styled.div`
  padding: 20px;
  background-color: var(--background);
  border-radius: 8px;
`;

export const ItemsSection = styled.div``;

export const ItemsContainer = styled.div`
  border: 1px solid var(--accent-light);
  border-radius: 8px;
  overflow: hidden;
`;

export const OrderItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background-color: ${(props) =>
    props.$index % 2 === 0 ? "var(--surface)" : "var(--background)"};
  border-bottom: ${(props) =>
    props.$isLast ? "none" : "1px solid var(--accent-light)"};
`;

export const ItemInfo = styled.div``;

export const ItemTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 5px;
`;

export const ItemDescription = styled.div`
  font-size: 12px;
  color: var(--text-muted);
`;

export const ItemPrice = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--success);
`;

// Order Summary Components
export const OrderSummary = styled.div`
  padding: 20px;
  background-color: var(--accent-light);
  border-radius: 8px;
  border: 1px solid var(--accent);
`;

export const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
`;

export const SummaryLabel = styled.span`
  color: var(--text-secondary);
`;

export const SummaryValue = styled.span`
  font-weight: 600;
  color: ${(props) => {
    if (props.$error) return "var(--error)";
    if (props.$accent) return "var(--accent)";
    return "var(--text-primary)";
  }};
`;

export const TotalRow = styled(SummaryRow)`
  border-top: 1px solid var(--accent);
  padding-top: 10px;
  font-size: 18px;
  font-weight: 600;
`;

export const ModalActions = styled.div`
  margin-top: 25px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;
